/* eslint-disable max-lines-per-function */

import { Routes, Route, Outlet } from "react-router-dom";
import React, { useEffect } from "react";
import FreshChatWidget from "components/freshchat-widget";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Viewer } from "components/auth/types";
import { initializeUser } from "services/analytics";
import { MainLayout } from "pages/layouts";
import NotFound from "pages/not-found";
import WaitlistUpdate from "pages/waitlist-update/dashboard/components/index";
import WaitlistUpdateContact from "pages/waitlist-update/manual-contact/components/index";
import WaitlistUpdateRemovals from "pages/waitlist-update/process-removals/components/index";
import { Reports, Duplicates } from "pages";
import WaitlistUpdateContainer from "pages/waitlist-update/container";
import { UserRole } from "types/gql-types";
import Messages from "../messages";

import { ApplicantProfileView } from "../interests-profile/components";
import SettingsPage from "../settings";
import InterestsNew from "../interests-new";
import InterestsByList from "../interests";
import InterestListRedirect from "../lists";
import { SidebarFiltersProvider } from "../interests/sidebar-filters";
import { InterestsTableProvider } from "../interests/interests-table/table-context";

type Props = { viewer: Viewer }

const ManagerDashboard: React.FC<Props> = ({ viewer }) => {
  if (!viewer) return null;
  useEffect(() => {
    const { id: userId, email, fullName, roles, organizationId, properties } = viewer;
    const role = roles.indexOf(UserRole.SUPERVISOR) > -1 ? "supervisor" : roles[0];
    const { id: propertyId, name: propertyName } = properties[0] || {};
    const onInterestsDashboard = !!location.pathname.match("/dash/manager/lists/");

    // don't initialize if we're curently on the interests dashboard page as this page will
    // call initializeUserLogin on page load, otherwise this will set the property ID to
    // the first available property which can cause incorrect tracking
    if (
      email &&
      fullName &&
      organizationId &&
      userId &&
      propertyId &&
      propertyName &&
      !onInterestsDashboard
    ) {
      initializeUser({
        email,
        fullName,
        organizationId,
        roles: [role],
        userId,
        propertyId,
        propertyName,
      });
    }
  }, []);

  return (
    <MainLayout backgroundColor="gray" showHeader showFooter={false}>
      <FreshChatWidget viewer={viewer} />
      <Routes>
        {/* Interests domain (wrap with providers) */}
        <Route element={<SidebarFiltersProvider>
          <InterestsTableProvider>
            <Outlet />
          </InterestsTableProvider>
        </SidebarFiltersProvider>}>
          <Route path="lists" element={<InterestListRedirect viewer={viewer} />} />
          <Route path="lists/:listId/p/:p/*" element={<InterestsByList viewer={viewer} />} />
          <Route path="lists/:listId/*" element={<InterestsByList viewer={viewer} />} />
          <Route path="interest/:interestID/*" element={<ApplicantProfileView path="" viewer={viewer} />} />
        </Route>

        {/* Other manager routes (no interests providers) */}
        <Route path="interests/:listId/new" element={<InterestsNew />} />
        <Route path="update" element={<WaitlistUpdate path="" viewer={viewer} />} />
        <Route path="update/:listId/*" element={<WaitlistUpdateContainer viewer={viewer} />}>
          <Route index element={<WaitlistUpdate viewer={viewer} />} />
          <Route path="contact/*" element={<WaitlistUpdateContact viewer={viewer} />} />
          <Route path="process/*" element={<WaitlistUpdateRemovals viewer={viewer} />} />
          <Route path="*" element={<WaitlistUpdate viewer={viewer} />} />
        </Route>

        <Route path="messages/*" element={<Messages viewer={viewer} />} />

        <Route path="reports/*" element={<Reports viewer={viewer} />} />

        <Route path="settings/*" element={<SettingsPage viewer={viewer} />} />

        <Route path="/duplicates/:interestListId/*" element={<Duplicates viewer={viewer} />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </MainLayout>
  );
};

export default authorizeForRole<Record<string, unknown>>(
  [UserRole.PROPERTY_MANAGER, UserRole.SUPERVISOR],
  ManagerDashboard as unknown as React.FC<Record<string, unknown>>,
);
